import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useForm, type UseFormRegister } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PiFloppyDiskBold } from 'react-icons/pi';
import { Button, LoadingState, PageHeader } from '@v2/design-system';
import api from '@v2/lib/api';
import { cn } from '@v2/utils/cn';

type Option = { value: string; label: string };
type IdOption = { id: number; name: string };

type FormOptions = {
    stages: Option[];
    modalities: string[];
    contract_types: string[];
    import_cnpjs: { value: string; label: string }[];
    suppliers: string[];
    cargo_agents: string[];
    freight_types: string[];
    process_statuses: string[];
    delivery_on_cd_arrival: string[];
    clients: IdOption[];
    vessels: IdOption[];
};

type ShipmentFormValues = {
    order_number: string;
    process_code?: string | null;
    process_name?: string | null;
    stage?: string | null;
    process_status?: string | null;
    finished?: boolean;
    order_date?: string | null;
    modality?: string | null;
    contract_type?: string | null;
    import_cnpj?: string | null;
    delivery_deadline?: string | null;
    shipping_deadline?: string | null;
    client_id?: number | null;
    client?: string | null;
    notes?: string | null;
    supplier?: string[];
    invoice?: string | null;
    invoice_financial_date?: string | null;
    financial_payment_date?: string | null;
    production_date?: string | null;
    supplier_balance_payment_date?: string | null;
    hyperlink?: string | null;
    freight_type?: string | null;
    order_ref?: string | null;
    cargo_agent?: string | null;
    shipping_line?: string | null;
    vessel_id?: number | null;
    vessel_voyage?: string | null;
    bl_number?: string | null;
    freight_value?: number | string | null;
    container_quantity?: number | null;
    containers?: string | null;
    etd_origin?: string | null;
    eta_destination?: string | null;
    eta_actual?: string | null;
    transit_time_carrier?: number | null;
    transit_time_actual?: number | null;
    customs_clearance_date?: string | null;
    free_time?: number | null;
    estimated_cd_date?: string | null;
    actual_cd_date?: string | null;
    delivery_on_cd_arrival?: string | null;
    preparation_date?: string | null;
    client_delivery_date?: string | null;
    delivery_address?: string | null;
    delivery_lat?: number | null;
    delivery_lng?: number | null;
    open_processes_ld?: number | null;
    deadline_days?: number | null;
};

const TABS = [
    { key: 'general', label: 'Embarque' },
    { key: 'financial', label: 'Financeiro' },
    { key: 'maritime', label: 'Marítimo' },
    { key: 'national', label: 'Nacional' },
    { key: 'status', label: 'Status' },
] as const;

type TabKey = (typeof TABS)[number]['key'];

const inputCls = cn(
    'w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-800 outline-none transition',
    'focus:border-amber-400 focus:ring-1 focus:ring-amber-400/30',
    'dark:border-zinc-700 dark:bg-zinc-800/60 dark:text-zinc-200',
    'dark:focus:border-amber-500/60 dark:focus:ring-amber-500/20',
);

export default function ShipmentFormPage() {
    const { id } = useParams<{ id: string }>();
    const isNew = !id;
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [tab, setTab] = useState<TabKey>('general');
    const [error, setError] = useState<string | null>(null);

    const optionsQuery = useQuery({
        queryKey: ['shipments', 'form-options'],
        queryFn: async (): Promise<FormOptions> => (await api.get('/shipments/form-options')).data,
        staleTime: 5 * 60_000,
    });

    const shipmentQuery = useQuery({
        queryKey: ['shipments', id, 'edit'],
        enabled: !isNew,
        queryFn: async () => (await api.get(`/shipments/${id}`)).data.data ?? (await api.get(`/shipments/${id}`)).data,
    });

    const form = useForm<ShipmentFormValues>({
        defaultValues: {
            order_number: '',
            stage: 'pedido',
            process_status: 'Aberto',
            finished: false,
            supplier: [],
        },
    });
    const { register, handleSubmit, reset, watch, setValue, formState } = form;

    useEffect(() => {
        if (shipmentQuery.data) {
            const d = shipmentQuery.data;
            reset({
                ...d,
                supplier: Array.isArray(d.supplier) ? d.supplier : d.supplier ? [d.supplier] : [],
            });
        }
    }, [shipmentQuery.data, reset]);

    const mutation = useMutation({
        mutationFn: async (values: ShipmentFormValues) => {
            const payload = sanitize(values);
            if (isNew) {
                return (await api.post('/shipments', payload)).data;
            }
            return (await api.put(`/shipments/${id}`, payload)).data;
        },
        onSuccess: (resp) => {
            setError(null);
            queryClient.invalidateQueries({ queryKey: ['shipments'] });
            const newId = resp?.data?.id ?? resp?.id ?? id;
            navigate(newId ? `/shipments/${newId}` : '/shipments');
        },
        onError: (err: any) => {
            const message = err?.response?.data?.message ?? err?.message ?? 'Erro ao salvar.';
            setError(message);
        },
    });

    const onSubmit = handleSubmit((values) => mutation.mutate(values));

    const opts = optionsQuery.data;
    const supplierSelected = watch('supplier') ?? [];
    const processCode = watch('process_code');
    const orderNumber = watch('order_number');
    const titleLabel = processCode || orderNumber || '';

    if (!isNew && shipmentQuery.isLoading) {
        return <LoadingState variant="detail" />;
    }

    return (
        <div className="space-y-6">
            <PageHeader
                title={isNew ? 'Novo Embarque' : `Editar Embarque${titleLabel ? ` · ${titleLabel}` : ''}`}
                breadcrumb={[
                    { name: 'Embarques', href: '/shipments' },
                    ...(!isNew ? [{ name: titleLabel || `#${id}`, href: `/shipments/${id}` }] : []),
                    { name: isNew ? 'Novo' : 'Editar' },
                ]}
            >
                <div className="flex items-center gap-2">
                    <Link to={isNew ? '/shipments' : `/shipments/${id}`}>
                        <Button variant="outline" size="sm" as="span">
                            Cancelar
                        </Button>
                    </Link>
                    <Button size="sm" onClick={onSubmit} disabled={mutation.isPending} isLoading={mutation.isPending}>
                        <PiFloppyDiskBold className="me-1.5 size-4" />
                        Salvar
                    </Button>
                </div>
            </PageHeader>

            {error && (
                <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300">
                    {error}
                </div>
            )}

            <div className="flex gap-1 overflow-x-auto border-b border-gray-200 dark:border-zinc-800">
                {TABS.map((t) => (
                    <button
                        key={t.key}
                        type="button"
                        onClick={() => setTab(t.key)}
                        className={cn(
                            'shrink-0 rounded-t-xl px-4 py-2.5 text-sm font-semibold transition',
                            tab === t.key
                                ? 'border border-b-0 border-gray-200 bg-white text-gray-900 dark:border-zinc-700 dark:bg-zinc-900 dark:text-white'
                                : 'text-gray-500 hover:text-gray-800 dark:text-zinc-500 dark:hover:text-zinc-300',
                        )}
                    >
                        {t.label}
                    </button>
                ))}
            </div>

            <form
                onSubmit={onSubmit}
                className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm dark:border-zinc-800 dark:bg-zinc-900 md:p-6"
            >
                {tab === 'general' && (
                    <GeneralTab
                        register={register}
                        opts={opts}
                        supplierSelected={supplierSelected}
                        toggleSupplier={(s) => {
                            const cur = supplierSelected ?? [];
                            setValue(
                                'supplier',
                                cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s],
                                { shouldDirty: true },
                            );
                        }}
                    />
                )}
                {tab === 'financial' && <FinancialTab register={register} opts={opts} />}
                {tab === 'maritime' && <MaritimeTab register={register} opts={opts} />}
                {tab === 'national' && <NationalTab register={register} opts={opts} />}
                {tab === 'status' && <StatusTab register={register} opts={opts} />}
            </form>

            {formState.errors && Object.keys(formState.errors).length > 0 && (
                <p className="text-xs text-red-600 dark:text-red-400">Verifique os campos obrigatórios.</p>
            )}
        </div>
    );
}

type TabProps = {
    register: UseFormRegister<ShipmentFormValues>;
    opts: FormOptions | undefined;
};

function GeneralTab({
    register,
    opts,
    supplierSelected,
    toggleSupplier,
}: TabProps & { supplierSelected: string[]; toggleSupplier: (s: string) => void }) {
    return (
        <Grid>
            <Field label="Código do Processo">
                <input className={cn(inputCls, 'font-mono')} disabled {...register('process_code')} placeholder="Gerado ao salvar com fornecedor" />
            </Field>
            <Field label="Número do Pedido *">
                <input className={inputCls} {...register('order_number', { required: true })} />
            </Field>
            <Field label="Nome do Processo">
                <input className={inputCls} placeholder="Ex: Academia Fitness SP — Lote 2" {...register('process_name')} />
            </Field>
            <Field label="Estágio">
                <select className={inputCls} {...register('stage')}>
                    {(opts?.stages ?? []).map((s) => (
                        <option key={s.value} value={s.value}>{s.label}</option>
                    ))}
                </select>
            </Field>
            <Field label="Data do Pedido">
                <input type="date" className={inputCls} {...register('order_date')} />
            </Field>
            <Field label="Modalidade">
                <select className={inputCls} {...register('modality')}>
                    <option value="">—</option>
                    {(opts?.modalities ?? []).map((m) => (
                        <option key={m} value={m}>{m}</option>
                    ))}
                </select>
            </Field>
            <Field label="Tipo de Contrato">
                <select className={inputCls} {...register('contract_type')}>
                    <option value="">—</option>
                    {(opts?.contract_types ?? []).map((c) => (
                        <option key={c} value={c}>{c}</option>
                    ))}
                </select>
            </Field>
            <Field label="CNPJ Importador">
                <select className={inputCls} {...register('import_cnpj')}>
                    <option value="">—</option>
                    {(opts?.import_cnpjs ?? []).map((c) => (
                        <option key={c.value} value={c.value}>{c.label}</option>
                    ))}
                </select>
            </Field>
            <Field label="Prazo de Entrega">
                <input type="date" className={inputCls} {...register('delivery_deadline')} />
            </Field>
            <Field label="Prazo de Embarque">
                <input type="date" className={inputCls} {...register('shipping_deadline')} />
            </Field>
            <Field label="Cliente">
                <select className={inputCls} {...register('client_id', { setValueAs: (v) => (v === '' ? null : Number(v)) })}>
                    <option value="">—</option>
                    {(opts?.clients ?? []).map((c) => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                </select>
            </Field>
            <Field label="Cliente (texto livre)">
                <input className={inputCls} {...register('client')} />
            </Field>
            <Field label="Invoice">
                <input className={inputCls} {...register('invoice')} />
            </Field>
            <Field label="Fornecedor(es)" full>
                <div className="flex flex-wrap gap-2">
                    {(opts?.suppliers ?? []).map((s) => {
                        const active = supplierSelected.includes(s);
                        return (
                            <button
                                key={s}
                                type="button"
                                onClick={() => toggleSupplier(s)}
                                className={cn(
                                    'rounded-full border px-3 py-1 text-xs font-semibold transition',
                                    active
                                        ? 'border-amber-500 bg-amber-50 text-amber-800 dark:border-amber-500/50 dark:bg-amber-500/15 dark:text-amber-300'
                                        : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-400 dark:hover:border-zinc-600',
                                )}
                            >
                                {s}
                            </button>
                        );
                    })}
                    {(opts?.suppliers ?? []).length === 0 && (
                        <p className="text-sm text-gray-400 dark:text-zinc-500">Nenhum fornecedor cadastrado.</p>
                    )}
                </div>
            </Field>
            <Field label="Observações" full>
                <textarea rows={3} className={cn(inputCls, 'resize-none')} {...register('notes')} />
            </Field>
        </Grid>
    );
}

function FinancialTab({ register }: TabProps) {
    return (
        <Grid>
            <Field label="Invoice enviada ao Financeiro">
                <input type="date" className={inputCls} {...register('invoice_financial_date')} />
            </Field>
            <Field label="Pagamento Financeiro">
                <input type="date" className={inputCls} {...register('financial_payment_date')} />
            </Field>
            <Field label="Início da Produção">
                <input type="date" className={inputCls} {...register('production_date')} />
            </Field>
            <Field label="Saldo ao Fornecedor">
                <input type="date" className={inputCls} {...register('supplier_balance_payment_date')} />
            </Field>
            <Field label="Hyperlink Drive" full>
                <input className={inputCls} {...register('hyperlink')} />
            </Field>
            <Field label="Referência do Pedido">
                <input className={inputCls} {...register('order_ref')} />
            </Field>
        </Grid>
    );
}

function MaritimeTab({ register, opts }: TabProps) {
    return (
        <Grid>
            <Field label="Agente de Carga">
                <select className={inputCls} {...register('cargo_agent')}>
                    <option value="">—</option>
                    {(opts?.cargo_agents ?? []).map((c) => (
                        <option key={c} value={c}>{c}</option>
                    ))}
                </select>
            </Field>
            <Field label="Armador">
                <input className={inputCls} {...register('shipping_line')} />
            </Field>
            <Field label="Navio">
                <select className={inputCls} {...register('vessel_id', { setValueAs: (v) => (v === '' ? null : Number(v)) })}>
                    <option value="">—</option>
                    {(opts?.vessels ?? []).map((v) => (
                        <option key={v.id} value={v.id}>{v.name}</option>
                    ))}
                </select>
            </Field>
            <Field label="Viagem">
                <input className={inputCls} {...register('vessel_voyage')} />
            </Field>
            <Field label="BL">
                <input className={inputCls} {...register('bl_number')} />
            </Field>
            <Field label="Tipo de Frete">
                <select className={inputCls} {...register('freight_type')}>
                    <option value="">—</option>
                    {(opts?.freight_types ?? []).map((f) => (
                        <option key={f} value={f}>{f}</option>
                    ))}
                </select>
            </Field>
            <Field label="Valor do Frete (US$)">
                <input type="number" step="0.01" className={inputCls} {...register('freight_value')} />
            </Field>
            <Field label="Qtd Containers">
                <input type="number" className={inputCls} {...register('container_quantity', { setValueAs: numOrNull })} />
            </Field>
            <Field label="Containers" full>
                <textarea rows={3} className={cn(inputCls, 'resize-none')} {...register('containers')} />
            </Field>
            <Field label="ETD Origem">
                <input type="date" className={inputCls} {...register('etd_origin')} />
            </Field>
            <Field label="ETA Destino">
                <input type="date" className={inputCls} {...register('eta_destination')} />
            </Field>
            <Field label="ETA Real">
                <input type="date" className={inputCls} {...register('eta_actual')} />
            </Field>
            <Field label="Trânsito Armador (dias)">
                <input type="number" className={inputCls} {...register('transit_time_carrier', { setValueAs: numOrNull })} />
            </Field>
            <Field label="Trânsito Real (dias)">
                <input type="number" className={inputCls} {...register('transit_time_actual', { setValueAs: numOrNull })} />
            </Field>
            <Field label="Desembaraço">
                <input type="date" className={inputCls} {...register('customs_clearance_date')} />
            </Field>
            <Field label="Free Time (dias)">
                <input type="number" className={inputCls} {...register('free_time', { setValueAs: numOrNull })} />
            </Field>
        </Grid>
    );
}

function NationalTab({ register, opts }: TabProps) {
    return (
        <Grid>
            <Field label="CD Estimado">
                <input type="date" className={inputCls} {...register('estimated_cd_date')} />
            </Field>
            <Field label="CD Real">
                <input type="date" className={inputCls} {...register('actual_cd_date')} />
            </Field>
            <Field label="Entrega na Chegada do CD">
                <select className={inputCls} {...register('delivery_on_cd_arrival')}>
                    <option value="">—</option>
                    {(opts?.delivery_on_cd_arrival ?? []).map((v) => (
                        <option key={v} value={v}>{v}</option>
                    ))}
                </select>
            </Field>
            <Field label="Data de Preparação">
                <input type="date" className={inputCls} {...register('preparation_date')} />
            </Field>
            <Field label="Entrega ao Cliente">
                <input type="date" className={inputCls} {...register('client_delivery_date')} />
            </Field>
            <Field label="Endereço de Entrega" full>
                <textarea rows={2} className={cn(inputCls, 'resize-none')} {...register('delivery_address')} />
            </Field>
            <Field label="Lat">
                <input type="number" step="any" className={inputCls} {...register('delivery_lat', { setValueAs: numOrNull })} />
            </Field>
            <Field label="Lng">
                <input type="number" step="any" className={inputCls} {...register('delivery_lng', { setValueAs: numOrNull })} />
            </Field>
        </Grid>
    );
}

function StatusTab({ register, opts }: TabProps) {
    return (
        <Grid>
            <Field label="Status do Processo">
                <select className={inputCls} {...register('process_status')}>
                    {(opts?.process_statuses ?? []).map((s) => (
                        <option key={s} value={s}>{s}</option>
                    ))}
                </select>
            </Field>
            <Field label="Processos Abertos LD">
                <input type="number" className={inputCls} {...register('open_processes_ld', { setValueAs: numOrNull })} />
            </Field>
            <Field label="Prazo (dias)">
                <input type="number" className={inputCls} {...register('deadline_days', { setValueAs: numOrNull })} />
            </Field>
            <Field label="Finalizado">
                <label className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-zinc-300">
                    <input
                        type="checkbox"
                        className="size-4 rounded border-gray-300 text-amber-500 focus:ring-amber-400 dark:border-zinc-600 dark:bg-zinc-800"
                        {...register('finished')}
                    />
                    Marcar embarque como concluído
                </label>
            </Field>
        </Grid>
    );
}

function Grid({ children }: { children: React.ReactNode }) {
    return <div className="grid gap-4 md:grid-cols-2">{children}</div>;
}

function Field({ label, children, full }: { label: string; children: React.ReactNode; full?: boolean }) {
    return (
        <div className={cn('flex flex-col gap-1', full && 'md:col-span-2')}>
            <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 dark:text-zinc-500">
                {label}
            </label>
            {children}
        </div>
    );
}

function numOrNull(v: unknown): number | null {
    if (v === '' || v === null || v === undefined) return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
}

function sanitize(values: ShipmentFormValues): Record<string, unknown> {
    const out: Record<string, unknown> = { ...values };
    // process_code é gerado no backend — não sobrescrever via form
    delete out.process_code;
    for (const key of Object.keys(out)) {
        if (out[key] === '') out[key] = null;
    }
    return out;
}
